import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Button,
  Text,
  Spinner,
  Combobox,
  Option,
  Persona,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  type ComboboxProps,
} from '@coreai/fluentui-react';
import { DismissRegular, EditRegular, ShieldKeyholeRegular } from '@fluentui/react-icons';
import { useSearchUsers, type User } from '@/hooks/useUserPermissions';
import { useUserPhoto } from '@/hooks/useUserPhoto';
import { useAuth } from '@/auth/useAuth';
import { comboboxListboxStyles } from '@/styles/comboboxListbox';

export type { User };

// Hoisted: stable across renders so Fluent's positioning hook doesn't
// recompute / reposition the listbox on every keystroke.
const COMBOBOX_POSITIONING = {
  matchTargetSize: 'width' as const,
  position: 'below' as const,
  align: 'start' as const,
};

const useStyles = makeStyles({
  userSearchContainer: {
    marginBottom: tokens.spacingVerticalL,
  },
  comboboxWrapper: {
    // Block-level so the input fills its container; the popup uses
    // matchTargetSize: 'width' to mirror this exact width.
    position: 'relative',
    width: '100%',
  },
  combobox: {
    width: '100%',
  },
  // Cap + thin-scrollbar shared with SearchableCombobox so the user-search
  // popup never escapes the viewport even when Graph returns many matches.
  listbox: {
    ...comboboxListboxStyles,
    // Concentric with the Combobox input radius so the popup reads as the
    // same surface as the trigger.
    borderRadius: tokens.borderRadiusMedium,
    // Defensive: even with `matchTargetSize: 'width'` Fluent's positioning
    // sometimes lets a Persona-based Option push the listbox wider than the
    // input. Clamp the box and force each row to live within it so the
    // avatars stay anchored to the left edge under the input.
    boxSizing: 'border-box',
    maxWidth: '100vw',
    '& [role="option"]': {
      minWidth: 0,
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
    // Truncate the Persona's name + email instead of stretching the row.
    '& .fui-Persona': {
      minWidth: 0,
      width: '100%',
    },
    '& .fui-Persona__primaryText, & .fui-Persona__secondaryText': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  clearButton: {
    position: 'absolute',
    right: tokens.spacingHorizontalS,
    top: '50%',
    transform: 'translateY(-50%)',
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXS,
  },
  authMessageBar: {
    marginBottom: tokens.spacingVerticalM,
  },
  selectedUserInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalL,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  flexFill: {
    flex: 1,
    // Allow the Persona to truncate long names/emails instead of pushing
    // the trailing badge + buttons off-screen.
    minWidth: 0,
  },
});

/** Reusable user search dropdown component */
export interface UserSearchDropdownProps {
  onUserSelect: (user: User) => void;
  placeholder?: string;
  /** Ref to the underlying input for focus restoration */
  inputRef?: React.Ref<HTMLInputElement>;
  /**
   * Accessible name for the underlying Combobox. Required for AT users —
   * the visible heading next to this control (e.g., "Select user", "Check
   * access") is a separate `<Text>` element, so the combobox has no
   * accessible name on its own (WCAG 3.3.2 Labels or Instructions and
   * 4.1.2 Name, Role, Value). Falls back to `placeholder` so existing
   * callers don't regress to "no name at all".
   */
  ariaLabel?: string;
  /**
   * Optional class merged onto the outer container — lets a caller override
   * the default block-level `marginBottom` / width when the dropdown lives
   * inline (e.g., in a flex header row).
   */
  className?: string;
}

export const UserSearchDropdown = ({
  onUserSelect,
  placeholder = 'Search for a user...',
  inputRef,
  ariaLabel,
  className,
}: UserSearchDropdownProps) => {
  const styles = useStyles();
  const { grantGraphAccess } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [granting, setGranting] = useState(false);
  const { data: searchResults, isLoading: isSearching, error, refetch } = useSearchUsers(debouncedQuery);

  // Detect auth errors (AADSTS65001 consent required, or missing token)
  const isAuthError = error?.message?.includes('consent') ||
    error?.message?.includes('AADSTS65001') ||
    error?.message?.includes('No access token') ||
    (error?.cause === 'AuthenticationError');

  // Handle grant access button click
  const handleGrantAccess = useCallback(async (e: React.MouseEvent) => {
    // Prevent event bubbling that might interfere with dialog focus management
    e.stopPropagation();
    e.preventDefault();
    setGranting(true);
    try {
      const token = await grantGraphAccess();
      if (token) {
        await refetch();
      }
    } finally {
      setGranting(false);
    }
  }, [grantGraphAccess, refetch]);

  // Debounce the search query by 250ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      // Open dropdown only after debounce when there's a query
      if (searchQuery.length > 0) {
        setIsOpen(true);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchChange: ComboboxProps['onChange'] = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    // Close dropdown while typing (will reopen after debounce)
    if (value.length === 0) {
      setIsOpen(false);
      setDebouncedQuery('');
    }
  };

  const handleUserSelect: ComboboxProps['onOptionSelect'] = (_event, data) => {
    const user = searchResults?.find((u) => u.id === data.optionValue);
    if (user) {
      onUserSelect(user);
      setSearchQuery('');
      setDebouncedQuery('');
      setIsOpen(false);
    }
  };

  const handleOpenChange: ComboboxProps['onOpenChange'] = (_event, data) => {
    // Only allow opening if there's a debounced query (API was called)
    setIsOpen(data.open && debouncedQuery.length > 0);
  };

  const handleClear = () => {
    setSearchQuery('');
    setDebouncedQuery('');
    setIsOpen(false);
  };

  const showNoResults = debouncedQuery.length >= 1 && !isSearching && (!searchResults || searchResults.length === 0) && !error;

  // Stable object refs for Combobox slot/positioning props — inline literals
  // would be new identities each render and force the popover to recompute.
  const listboxProp = useMemo(() => ({ className: styles.listbox }), [styles.listbox]);

  return (
    <div className={mergeClasses(styles.userSearchContainer, className)}>
      {/* Auth error state - show authorize button */}
      {isAuthError && (
        <MessageBar intent="warning" className={styles.authMessageBar}>
          <MessageBarBody>
            User search requires authorization. Click Authorize to grant access.
          </MessageBarBody>
          <MessageBarActions>
            <Button
              appearance="transparent"
              size="small"
              icon={<ShieldKeyholeRegular />}
              onClick={handleGrantAccess}
              disabled={granting}
            >
              {granting ? 'Authorizing...' : 'Authorize'}
            </Button>
          </MessageBarActions>
        </MessageBar>
      )}
      <div className={styles.comboboxWrapper}>
        <Combobox
          input={{ ref: inputRef }}
          className={styles.combobox}
          listbox={listboxProp}
          positioning={COMBOBOX_POSITIONING}
          placeholder={placeholder}
          aria-label={ariaLabel ?? placeholder}
          value={searchQuery}
          open={isOpen}
          onOpenChange={handleOpenChange}
          onChange={handleSearchChange}
          onOptionSelect={handleUserSelect}
          freeform
          {...(searchQuery.length > 0 ? { expandIcon: null } : {})}
        >
          {isSearching ? (
            <Option key="loading" disabled text="Searching...">
              <Spinner size="tiny" /> Searching...
            </Option>
          ) : searchResults && searchResults.length > 0 ? (
            searchResults.map((user) => (
              <Option key={user.id} value={user.id} text={user.displayName}>
                <Persona
                  name={user.displayName}
                  secondaryText={user.email}
                  size="small"
                  avatar={{ color: 'colorful' }}
                />
              </Option>
            ))
          ) : showNoResults ? (
            <Option key="no-results" disabled text="No users found">
              <Text style={{ color: tokens.colorNeutralForeground3 }}>No users found</Text>
            </Option>
        ) : null}
      </Combobox>
      {searchQuery.length > 0 && (
        <Button
          className={styles.clearButton}
          appearance="subtle"
          size="small"
          icon={<DismissRegular />}
          onClick={handleClear}
          aria-label="Clear search"
        />
      )}
      </div>
    </div>
  );
};

UserSearchDropdown.displayName = 'UserSearchDropdown';

/**
 * Persona for any user, with their Microsoft Graph profile photo when
 * available. Falls back to the colorful initials avatar when the user has
 * no photo, hasn't consented to Graph access, or we're running in E2E
 * mode (where photo fetches are skipped to keep snapshots deterministic).
 *
 * Note: this calls a query hook per-instance. Each Persona triggers one
 * `/users/{id}/photo/$value` fetch, deduplicated by react-query keyed on
 * (userId, tenantId) with 1h staleTime, so a re-render of the same user
 * list reuses the cache.
 */
export interface UserPersonaProps {
  user: User;
  size?: React.ComponentProps<typeof Persona>['size'];
  className?: string;
}

export const UserPersona = ({ user, size = 'small', className }: UserPersonaProps) => {
  const photoUrl = useUserPhoto(user.id);
  // Stable ref so Persona's internal memoization isn't busted by a new
  // avatar object identity on every parent render.
  const avatar = useMemo(
    () => (photoUrl ? { image: { src: photoUrl } } : { color: 'colorful' as const }),
    [photoUrl],
  );
  return (
    <Persona
      name={user.displayName}
      secondaryText={user.email}
      size={size}
      avatar={avatar}
      className={className}
    />
  );
};

/** Displays selected user info with optional badge and clear button */
export interface SelectedUserDisplayProps {
  user: User;
  badge?: React.ReactNode;
  /**
   * Callback when the clear (X) button is pressed. Omit to hide the clear
   * button entirely — used by the Edit role-assignment dialog where the
   * user is locked in and the dialog itself is the only exit.
   */
  onClear?: () => void;
  /** Whether to show the edit button */
  canEdit?: boolean;
  /** Callback when edit button is clicked */
  onEdit?: () => void;
  /** Whether trailing actions should be disabled */
  actionsDisabled?: boolean;
}

export const SelectedUserDisplay = ({
  user,
  badge,
  onClear,
  canEdit,
  onEdit,
  actionsDisabled = false,
}: SelectedUserDisplayProps) => {
  const styles = useStyles();
  return (
    <div className={styles.selectedUserInfo}>
      <UserPersona user={user} size="medium" className={styles.flexFill} />
      {badge}
      {canEdit && onEdit && (
        <Button
          appearance="subtle"
          size="small"
          icon={<EditRegular />}
          onClick={onEdit}
          aria-label="Edit permissions"
          disabled={actionsDisabled}
        />
      )}
      {onClear && (
        <Button
          appearance="subtle"
          size="small"
          icon={<DismissRegular />}
          onClick={onClear}
          aria-label="Clear selection"
          disabled={actionsDisabled}
        />
      )}
    </div>
  );
};

SelectedUserDisplay.displayName = 'SelectedUserDisplay';
